// Guest cart. Lives entirely client-side until the user signs in, then is
// replayed into the server-side cart via mergeGuestCart() and cleared.
//
// We hold a denormalised snapshot of each line — itemId, name, qty, unit
// price at the time it was added — so the cart UI can render before the
// server cart exists.
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { GUEST_SESSION_STORAGE_KEY } from '@core/constants/auth';
import { getCartLineSelectionSignature } from '@core/utils/cartSelections';

/**
 * @typedef {Object} GuestCartLine
 * @property {string} lineId        client-only nanoid-ish key
 * @property {string} itemId
 * @property {string} itemName
 * @property {number} quantity
 * @property {number} unitPrice     minor units (snapshot)
 * @property {number} [modifierTotal]
 * @property {string} [imageUrl]
 * @property {string} [uom]
 * @property {object} [variant]
 * @property {object[]} [modifiers]
 * @property {object[]} [selectedVariants]
 * @property {object[]} [selectedModifiers]
 * @property {object[]} [serviceItems]
 */

const emptyLines = /** @type {GuestCartLine[]} */ ([]);
const newLineId = () => `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const newSessionId = () =>
  `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

function lineMatches(a, b) {
  return (
    a.itemId === b.itemId && getCartLineSelectionSignature(a) === getCartLineSelectionSignature(b)
  );
}

export const useGuestCartStore = create(
  persist(
    (set, get) => ({
      lines: emptyLines,
      sessionId: newSessionId(),

      ensureSession: () => {
        const sessionId = get().sessionId;
        if (sessionId) return sessionId;
        const next = newSessionId();
        set({ sessionId: next });
        return next;
      },

      add: (input) =>
        set((s) => {
          const existing = s.lines.find((l) => lineMatches(l, input));
          if (existing) {
            return {
              lines: s.lines.map((l) =>
                l.lineId === existing.lineId
                  ? {
                      ...l,
                      quantity: l.quantity + (input.quantity ?? 1),
                      modifierTotal:
                        (l.modifierTotal ?? 0) + (input.modifierTotal ?? input.modExtra ?? 0),
                    }
                  : l,
              ),
            };
          }
          return {
            lines: [
              ...s.lines,
              {
                ...input,
                modifierTotal: input.modifierTotal ?? input.modExtra ?? 0,
                lineId: newLineId(),
              },
            ],
          };
        }),

      updateQuantity: (lineId, quantity) =>
        set((s) => ({
          lines:
            quantity <= 0
              ? s.lines.filter((l) => l.lineId !== lineId)
              : s.lines.map((l) => (l.lineId === lineId ? { ...l, quantity } : l)),
        })),

      remove: (lineId) => set((s) => ({ lines: s.lines.filter((l) => l.lineId !== lineId) })),

      clear: () => set({ lines: [] }),

      summary: () => {
        const lines = get().lines;
        const subtotal = lines.reduce(
          (sum, l) => sum + l.unitPrice * l.quantity + (l.modifierTotal ?? 0),
          0,
        );
        const count = lines.reduce((sum, l) => sum + l.quantity, 0);
        return { count, subtotal };
      },
    }),
    {
      name: GUEST_SESSION_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        lines: state.lines,
        sessionId: state.sessionId,
      }),
    },
  ),
);
